import { useQuery } from "@tanstack/react-query";

import { JsonView } from "@/components/json-view";
import { Panel } from "@/components/panel";
import { QueryErrorState } from "@/components/query-error-state";
import { StatCard } from "@/components/stat-card";
import { apiGet } from "@/lib/api-client";
import { asCurrency } from "@/lib/format";
import { asRecord, asArray } from "@/lib/type-utils";

export function CostCenterPage() {
  const costsQuery = useQuery({
    queryKey: ["cost-center", "costs"],
    queryFn: () => apiGet<unknown>("/api/costs/summary"),
    refetchInterval: 60_000,
  });

  const usageQuery = useQuery({
    queryKey: ["cost-center", "usage"],
    queryFn: () => apiGet<unknown>("/api/usage"),
    refetchInterval: 60_000,
  });

  const costs = asRecord(costsQuery.data);
  const usage = asRecord(usageQuery.data);

  // /api/costs/summary always answers HTTP 200 with success/available flags;
  // a degraded read (HTTP failure OR flagged-unavailable body) must never
  // render as $0.00 spend on a money page.
  const costsDegraded =
    costsQuery.isSuccess && (costs.success === false || costs.available === false);
  const costsUnavailable = costsQuery.isError || costsDegraded;
  // Degraded ≠ unreachable: the backend answered but its cost store can't be
  // read. Saying "Can't reach the backend" there is wrong (live dashboard,
  // 2026-06-12) — name the real condition and surface the backend's note.
  const costsErrorTitle = costsDegraded ? "Cost data store unavailable" : undefined;
  const costsErrorDetail = costsDegraded
    ? String(costs.note ?? costs.error ?? "")
    : undefined;

  const totalSpend = Number(costs.total_spend ?? costs.total ?? 0);
  const dailySpend = Number(costs.daily_spend ?? costs.daily ?? 0);
  const monthlySpend = Number(costs.monthly_spend ?? costs.monthly ?? 0);
  const budget = Number(costs.budget ?? costs.budget_limit ?? 1000);
  const budgetUsed = (monthlySpend / budget) * 100;

  const modelCosts = asArray(costs.by_model ?? costs.models);
  const endpointCosts = asArray(costs.by_endpoint ?? costs.endpoints);

  const totalRequests = Number(usage.total_requests ?? usage.requests ?? 0);
  const tokensInput = Number(usage.tokens_input ?? usage.input_tokens ?? 0);
  const tokensOutput = Number(usage.tokens_output ?? usage.output_tokens ?? 0);
  const totalTokens = tokensInput + tokensOutput;
  const avgCostPerRequest = totalRequests > 0 ? totalSpend / totalRequests : 0;

  return (
    <div className="space-y-4 md:space-y-6">
      <Panel title="Cost Center" description="AI API spend tracking, usage analytics, and budget monitoring.">
        {costsUnavailable ? (
          <QueryErrorState what="cost summary" title={costsErrorTitle} detail={costsErrorDetail} onRetry={() => void costsQuery.refetch()} />
        ) : (
          <div className="grid gap-2 md:gap-3 grid-cols-2 md:grid-cols-[repeat(auto-fit,minmax(11rem,1fr))]">
            <StatCard label="Total Spend (All Time)" value={asCurrency(totalSpend)} hint="Cumulative API costs" />
            <StatCard
              label="Daily Spend"
              value={asCurrency(dailySpend)}
              tone={dailySpend > budget / 30 ? "warning" : "default"}
              hint="Last 24 hours"
            />
            <StatCard
              label="Monthly Spend"
              value={asCurrency(monthlySpend)}
              tone={budgetUsed > 90 ? "warning" : budgetUsed > 75 ? "warning" : "default"}
              hint={`${budgetUsed.toFixed(1)}% of budget`}
            />
            <StatCard label="Budget Limit" value={asCurrency(budget)} hint="Monthly spending cap" />
            <StatCard label="Avg Cost/Request" value={asCurrency(avgCostPerRequest)} hint="Mean cost per API call" />
          </div>
        )}
      </Panel>

      <Panel title="Budget Progress" description="Monthly spending against budget limit">
        {costsUnavailable ? (
          <QueryErrorState what="budget status" title={costsErrorTitle} detail={costsErrorDetail} onRetry={() => void costsQuery.refetch()} />
        ) : (
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-slate-400">Budget Used</span>
              <span className={budgetUsed > 90 ? "text-red-400" : budgetUsed > 75 ? "text-yellow-400" : "text-green-400"}>
                {budgetUsed.toFixed(1)}%
              </span>
            </div>
            <div className="h-4 rounded-full bg-gunmetal">
              <div
                className={`h-full rounded-full transition-all ${
                  budgetUsed > 90 ? "bg-red-500" : budgetUsed > 75 ? "bg-yellow-500" : "bg-green-500"
                }`}
                style={{ width: `${Math.min(budgetUsed, 100)}%` }}
              />
            </div>
            <div className="flex justify-between text-xs text-slate-400">
              <span>{asCurrency(monthlySpend)} spent</span>
              <span>{asCurrency(budget)} limit</span>
            </div>
          </div>
        )}
      </Panel>

      <div className="grid gap-3 md:gap-4 grid-cols-1 lg:grid-cols-2">
        <Panel title="Cost by Model" description="API spend breakdown by AI model">
          <div className="max-h-[300px] space-y-2 overflow-y-auto">
            {costsUnavailable ? (
              <QueryErrorState what="model cost breakdown" title={costsErrorTitle} detail={costsErrorDetail} onRetry={() => void costsQuery.refetch()} />
            ) : modelCosts.length === 0 ? (
              <div className="rounded-lg bg-gunmetal/50 p-4 text-center text-sm text-slate-400">
                No model cost data available
              </div>
            ) : (
              modelCosts.map((model, index) => {
                const cost = Number(model.cost ?? model.spend ?? 0);
                const modelTotal = modelCosts.reduce((sum, m) => sum + Number(m.cost ?? m.spend ?? 0), 0);
                const percentage = modelTotal > 0 ? (cost / modelTotal) * 100 : 0;

                return (
                  <div key={index} className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">{String(model.model ?? model.name ?? "Unknown")}</span>
                      <span className="text-sm text-gold-400">{asCurrency(cost)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="h-2 flex-1 rounded-full bg-gunmetal">
                        <div className="h-full rounded-full bg-gold-500" style={{ width: `${percentage}%` }} />
                      </div>
                      <span className="mono text-xs text-slate-400 w-10 text-right">{percentage.toFixed(1)}%</span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </Panel>

        <Panel title="Cost by Endpoint" description="API spend breakdown by endpoint">
          <div className="max-h-[300px] space-y-2 overflow-y-auto">
            {costsUnavailable ? (
              <QueryErrorState what="endpoint cost breakdown" title={costsErrorTitle} detail={costsErrorDetail} onRetry={() => void costsQuery.refetch()} />
            ) : endpointCosts.length === 0 ? (
              <div className="rounded-lg bg-gunmetal/50 p-4 text-center text-sm text-slate-400">
                No endpoint cost data available
              </div>
            ) : (
              endpointCosts.map((endpoint, index) => {
                const cost = Number(endpoint.cost ?? endpoint.spend ?? 0);
                const endpointTotal = endpointCosts.reduce(
                  (sum, e) => sum + Number(e.cost ?? e.spend ?? 0),
                  0,
                );
                const percentage = endpointTotal > 0 ? (cost / endpointTotal) * 100 : 0;

                return (
                  <div key={index} className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">{String(endpoint.endpoint ?? endpoint.path ?? "Unknown")}</span>
                      <span className="text-sm text-gold-400">{asCurrency(cost)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="h-2 flex-1 rounded-full bg-gunmetal">
                        <div className="h-full rounded-full bg-cyan-500" style={{ width: `${percentage}%` }} />
                      </div>
                      <span className="mono text-xs text-slate-400 w-10 text-right">{percentage.toFixed(1)}%</span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </Panel>
      </div>

      <Panel title="Token Usage" description="API token consumption metrics">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          <div className="rounded-lg bg-gunmetal/50 p-3 text-center">
            <div className="text-lg font-bold text-gold-400">{totalRequests.toLocaleString()}</div>
            <div className="text-xs text-slate-400">Total Requests</div>
          </div>
          <div className="rounded-lg bg-gunmetal/50 p-3 text-center">
            <div className="text-lg font-bold text-cyan-400">{tokensInput.toLocaleString()}</div>
            <div className="text-xs text-slate-400">Input Tokens</div>
          </div>
          <div className="rounded-lg bg-gunmetal/50 p-3 text-center">
            <div className="text-lg font-bold text-cyan-400">{tokensOutput.toLocaleString()}</div>
            <div className="text-xs text-slate-400">Output Tokens</div>
          </div>
          <div className="rounded-lg bg-gunmetal/50 p-3 text-center">
            <div className="text-lg font-bold text-gold-400">{totalTokens.toLocaleString()}</div>
            <div className="text-xs text-slate-400">Total Tokens</div>
          </div>
          <div className="rounded-lg bg-gunmetal/50 p-3 text-center">
            {/* Cost-derived tiles read the costs query; a degraded read must
                not render as $0. */}
            <div className="text-lg font-bold text-gold-400">
              {costsUnavailable ? "n/a" : `$${avgCostPerRequest.toFixed(4)}`}
            </div>
            <div className="text-xs text-slate-400">Cost/Request</div>
          </div>
          <div className="rounded-lg bg-gunmetal/50 p-3 text-center">
            <div className="text-lg font-bold text-gold-400">
              {costsUnavailable ? "n/a" : `$${(totalSpend / (totalTokens / 1000000 || 1)).toFixed(2)}`}
            </div>
            <div className="text-xs text-slate-400">Cost/1M Tokens</div>
          </div>
        </div>
      </Panel>

      <div className="grid gap-3 md:gap-4 grid-cols-1 lg:grid-cols-2">
        <Panel title="Costs API" description="Raw cost data">
          <JsonView value={costsQuery.data} />
        </Panel>
        <Panel title="Usage API" description="Raw usage data">
          <JsonView value={usageQuery.data} />
        </Panel>
      </div>
    </div>
  );
}
